import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../../../core/common/collapse-header";
import { all_routes } from "../../../../router/all_routes";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";
import apiDefinitions from "../../../../../core/api/apiDefinitions";

const route = all_routes;

// Interfaces
interface EmissionTest {
  id: number;
  numberPlate: string;
  serialNumber: string;
  issueDate: string;
  expiryDate: string;
  status: string;
}

interface EmissionTestDetails {
  id: number;
  numberPlate: string;
  ownerName: string;
  ownerAddress: string;
  vehicleClass: string;
  fuelType: string;
  grossWeight: number;
  noOfKms: number;
  serialNumber: string;
  issueDate: string;
  center: string;
  testFee: number;
  overallStatus: string;
  expiryDate: string;
  status: string;
  createdBy: string;
  createdAt: string;
}

interface Insurance {
  id: number;
  numberPlate: string;
  policyNumber: string;
  coverageStartDate: string;
  coverageEndDate: string;
  insurerCompanyName: string;
  status: string;
}

interface InsuranceDetails {
  id: number;
  numberPlate: string;
  insurerCompanyName: string;
  policyNumber: string;
  policyHolderName: string;
  policyHolderAddress: string;
  coverageStartDate: string;
  coverageEndDate: string;
  coverageType: string;
  issuedDate: string;
  status: string;
  createdBy: string;
  createdAt: string;
}

interface RevenueLicense {
  id: number;
  numberPlate: string;
  revenueLicenceNumber: string;
  licenceStartDate: string;
  licenceEndDate: string;
  status: string;
}

interface RevenueLicenseDetails {
  id: number;
  numberPlate: string;
  revenueLicenceNumber: string;
  vehicleClass: string;
  fuelType: string;
  ownerName: string;
  ownerAddress: string;
  grossWeight: number;
  annualFee: number;
  arrears: number;
  fines: number;
  totalAmount: number;
  licenceStartDate: string;
  licenceEndDate: string;
  issuedDate: string;
  status: string;
  createdBy: string;
  createdAt: string;
}

const VehicleDashboard: React.FC = () => {
  const navItems = [
    { label: "View Dashboard", icon: "ti ti-layout-dashboard", link: route.VehicleDashboard, permission: "SERVICE_MANAGER" },
    { label: "View Vehicles", icon: "ti ti-car", link: route.VehicleView, permission: "SERVICE_MANAGER" },
    { label: "Add Vehicle", icon: "ti ti-settings-cog", link: route.addvehicle, permission: "SERVICE_MANAGER" },
    { label: "Add Master Data", icon: "ti ti-car", link: route.AddMasterData, permission: "SERVICE_MANAGER" },
  ];

  // State for emission tests
  const [emissionData, setEmissionData] = useState<EmissionTest[]>([]);
  const [emissionError, setEmissionError] = useState<string | null>(null);
  const [isEmissionLoading, setIsEmissionLoading] = useState(false);

  // State for insurance
  const [insuranceData, setInsuranceData] = useState<Insurance[]>([]);
  const [insuranceError, setInsuranceError] = useState<string | null>(null);
  const [isInsuranceLoading, setIsInsuranceLoading] = useState(false);

  // State for revenue licenses
  const [revenueLicenseData, setRevenueLicenseData] = useState<RevenueLicense[]>([]);
  const [revenueLicenseError, setRevenueLicenseError] = useState<string | null>(null);
  const [isRevenueLoading, setIsRevenueLoading] = useState(false);

  // State for modals
  const [showEmissionModal, setShowEmissionModal] = useState(false);
  const [emissionDetails, setEmissionDetails] = useState<EmissionTestDetails | null>(null);
  const [emissionModalError, setEmissionModalError] = useState<string | null>(null);

  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState<InsuranceDetails | null>(null);
  const [insuranceModalError, setInsuranceModalError] = useState<string | null>(null);

  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [revenueLicenseDetails, setRevenueLicenseDetails] = useState<RevenueLicenseDetails | null>(null);
  const [revenueModalError, setRevenueModalError] = useState<string | null>(null);

  // Function to calculate days until expiry
  const calculateDaysUntilExpiry = (endDate: string): number => {
    const currentDate = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry.getTime() - currentDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : 0;
  };

  // Fetch emission data
  const fetchEmissionData = async () => {
    if (isEmissionLoading) return;
    setIsEmissionLoading(true);
    try {
      const response = await apiDefinitions.getLatestEmissionTests();
      if (response.data.status === "S1000" && response.data.data.length > 0) {
        setEmissionData(response.data.data);
        setEmissionError(null);
      } else {
        setEmissionData([]);
        setEmissionError("No emission test data available.");
      }
    } catch (error: any) {
      setEmissionData([]);
      setEmissionError("Failed to fetch emission test data. Please try again.");
    } finally {
      setIsEmissionLoading(false);
    }
  };

  // Fetch insurance data
  const fetchInsuranceData = async () => {
    if (isInsuranceLoading) return;
    setIsInsuranceLoading(true);
    try {
      const response = await apiDefinitions.getLatestInsurance();
      if (response.data.status === "S1000" && response.data.data.length > 0) {
        setInsuranceData(response.data.data);
        setInsuranceError(null);
      } else {
        setInsuranceData([]);
        setInsuranceError("No insurance data available.");
      }
    } catch (error: any) {
      setInsuranceData([]);
      setInsuranceError("Failed to fetch insurance data. Please try again.");
    } finally {
      setIsInsuranceLoading(false);
    }
  };

  // Fetch revenue license data
  const fetchRevenueLicenseData = async () => {
    if (isRevenueLoading) return;
    setIsRevenueLoading(true);
    try {
      const response = await apiDefinitions.getLatestRevenueLicences();
      if (response.data.status === "S1000" && response.data.data.length > 0) {
        setRevenueLicenseData(response.data.data);
        setRevenueLicenseError(null);
      } else {
        setRevenueLicenseData([]);
        setRevenueLicenseError("No revenue license data available.");
      }
    } catch (error: any) {
      setRevenueLicenseData([]);
      setRevenueLicenseError("Failed to fetch revenue license data. Please try again.");
    } finally {
      setIsRevenueLoading(false);
    }
  };

  // Fetch emission test details for modal
  // const fetchEmissionTestDetails = async (id: number) => {
  //   try {
  //     const response = await apiDefinitions.getEmissionTestById(id);
  //     if (response.data.status === "S1000") {
  //       setEmissionDetails(response.data.data);
  //       setEmissionModalError(null);
  //       setShowEmissionModal(true);
  //     } else {
  //       setEmissionModalError("Failed to retrieve emission test details.");
  //       setShowEmissionModal(true);
  //     }
  //   } catch (error: any) {
  //     setEmissionModalError("Failed to fetch emission test details. Please try again.");
  //     setShowEmissionModal(true);
  //   }
  // };

  // Fetch insurance details for modal
  const fetchInsuranceDetails = async (id: number) => {
    try {
      const response = await apiDefinitions.getInsuranceById(id);
      if (response.data.status === "S1000") {
        setInsuranceDetails(response.data.data);
        setInsuranceModalError(null);
        setShowInsuranceModal(true);
      } else {
        setInsuranceModalError("Failed to retrieve insurance details.");
        setShowInsuranceModal(true);
      }
    } catch (error: any) {
      setInsuranceModalError("Failed to fetch insurance details. Please try again.");
      setShowInsuranceModal(true);
    }
  };

  // Fetch revenue license details for modal
  const fetchRevenueLicenseDetails = async (id: number) => {
    try {
      const response = await apiDefinitions.getRevenueLicenceById(id);
      if (response.data.status === "S1000") {
        setRevenueLicenseDetails(response.data.data);
        setRevenueModalError(null);
        setShowRevenueModal(true);
      } else {
        setRevenueModalError("Failed to retrieve revenue license details.");
        setShowRevenueModal(true);
      }
    } catch (error: any) {
      setRevenueModalError("Failed to fetch revenue license details. Please try again.");
      setShowRevenueModal(true);
    }
  };

  // Handle modal close
  const handleCloseEmissionModal = () => {
    setShowEmissionModal(false);
    setEmissionDetails(null);
    setEmissionModalError(null);
  };

  const handleCloseInsuranceModal = () => {
    setShowInsuranceModal(false);
    setInsuranceDetails(null);
    setInsuranceModalError(null);
  };

  const handleCloseRevenueModal = () => {
    setShowRevenueModal(false);
    setRevenueLicenseDetails(null);
    setRevenueModalError(null);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchEmissionData();
    fetchInsuranceData();
    fetchRevenueLicenseData();
  }, []);

  // Get unique number plates and their combined data
  const getCombinedData = () => {
    const allNumberPlates = new Set([
      ...emissionData.map((e) => e.numberPlate),
      ...insuranceData.map((i) => i.numberPlate),
      ...revenueLicenseData.map((r) => r.numberPlate),
    ]);
    return Array.from(allNumberPlates).map((numberPlate) => {
      const emission = emissionData.find((e) => e.numberPlate === numberPlate) || null;
      const insurance = insuranceData.find((i) => i.numberPlate === numberPlate) || null;
      const revenue = revenueLicenseData.find((r) => r.numberPlate === numberPlate) || null;
      return { numberPlate, emission, insurance, revenue };
    });
  };

  // Calculate counts for cards
  const vehicleCount = getCombinedData().length;
  const expiredSoonCount = [
    ...insuranceData.filter((i) => calculateDaysUntilExpiry(i.coverageEndDate) > 0 && calculateDaysUntilExpiry(i.coverageEndDate) <= 7),
    ...emissionData.filter((e) => calculateDaysUntilExpiry(e.expiryDate) > 0 && calculateDaysUntilExpiry(e.expiryDate) <= 7),
    ...revenueLicenseData.filter((r) => calculateDaysUntilExpiry(r.licenceEndDate) > 0 && calculateDaysUntilExpiry(r.licenceEndDate) <= 7),
  ].length;
  const expiredCount = [
    ...insuranceData.filter((i) => calculateDaysUntilExpiry(i.coverageEndDate) <= 0),
    ...emissionData.filter((e) => calculateDaysUntilExpiry(e.expiryDate) <= 0),
    ...revenueLicenseData.filter((r) => calculateDaysUntilExpiry(r.licenceEndDate) <= 0),
  ].length;
  const totalDocuments = emissionData.length + insuranceData.length + revenueLicenseData.length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Vehicle Management/Vehicles/View Dashboard</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom">
                  {navItems.map((item, index) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-3" key={index}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${index === 0 ? "active" : ""}`}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>
            <div className="row mt-4" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }}>
                <div
                  className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                  style={{
                    transition: 'all 0.3s ease',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="p-6" style={{ padding: '1.5rem' }}>
                    <div className="flex items-center justify-between mb-4" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div
                        className="icon-container bg-blue-100"
                        style={{
                          height: '60px',
                          width: '60px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '12px',
                          backgroundColor: '#dbeafe',
                        }}
                      >
                        <i className="fas fa-car text-blue-500 text-2xl" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
                      </div>
                      <span className="text-3xl font-bold text-gray-800" id="vehicleCount" style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>
                        {vehicleCount}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0' }}>
                      Vehicle Count
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', margin: '0' }}>
                      Total vehicles in KIU fleet
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }}>
                <div
                  className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                  style={{
                    transition: 'all 0.3s ease',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="p-6" style={{ padding: '1.5rem' }}>
                    <div className="flex items-center justify-between mb-4" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div
                        className="icon-container bg-amber-100"
                        style={{
                          height: '60px',
                          width: '60px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '12px',
                          backgroundColor: '#fef3c7',
                        }}
                      >
                        <i className="fas fa-exclamation-triangle text-amber-500 text-2xl" style={{ fontSize: '1.5rem', color: '#f59e0b' }}></i>
                      </div>
                      <span className="text-3xl font-bold text-gray-800" id="expiredSoonCount" style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>
                        {expiredSoonCount}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0' }}>
                      Expired Soon
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', margin: '0' }}>
                      Doc expiring within 7 days
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }}>
                <div
                  className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                  style={{
                    transition: 'all 0.3s ease',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="p-6" style={{ padding: '1.5rem' }}>
                    <div className="flex items-center justify-between mb-4" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div
                        className="icon-container bg-red-100"
                        style={{
                          height: '60px',
                          width: '60px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '12px',
                          backgroundColor: '#fee2e2',
                        }}
                      >
                        <i className="fas fa-ban text-red-500 text-2xl" style={{ fontSize: '1.5rem', color: '#ef4444' }}></i>
                      </div>
                      <span className="text-3xl font-bold text-gray-800" id="expiredCount" style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>
                        {expiredCount}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0' }}>
                      Expired
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', margin: '0' }}>
                      Currently expired
                    </p>
                  </div>
                </div>
              </div>
              </div>
            <div className="row mt-4">
              <div className="col-12">
                <h6 className="mb-3">Vehicle Status Overview</h6>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>No Plate</th>
                        <th>Insurance Days</th>
                        <th>Insurance Status</th>
                        <th>Emission Days</th>
                        <th>Emission Status</th>
                        <th>Revenue Days</th>
                        <th>Revenue Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isEmissionLoading || isInsuranceLoading || isRevenueLoading ? (
                        <tr><td colSpan={14} className="text-center">Loading...</td></tr>
                      ) : emissionError || insuranceError || revenueLicenseError ? (
                        <tr><td colSpan={14} className="text-center text-warning">
                          {emissionError || insuranceError || revenueLicenseError}
                        </td></tr>
                      ) : getCombinedData().length === 0 ? (
                        <tr><td colSpan={14} className="text-center">No data available.</td></tr>
                      ) : (
                        getCombinedData().map((item) => (
                          <tr key={item.numberPlate}>
                            <td>{item.numberPlate}</td>
                            <td>{item.insurance ? calculateDaysUntilExpiry(item.insurance.coverageEndDate) : "N/A"}</td>
                            <td>
                              {item.insurance ? (
                                <span
                                  className={`badge ${
                                    calculateDaysUntilExpiry(item.insurance.coverageEndDate) <= 0
                                      ? "bg-danger"
                                      : calculateDaysUntilExpiry(item.insurance.coverageEndDate) <= 7
                                      ? "bg-warning"
                                      : "bg-success"
                                  }`}
                                >
                                  {calculateDaysUntilExpiry(item.insurance.coverageEndDate) <= 0
                                    ? "Expired"
                                    : calculateDaysUntilExpiry(item.insurance.coverageEndDate) <= 7
                                    ? "Expiring Soon"
                                    : "Active"}
                                </span>
                              ) : "N/A"}
                            </td>
                            <td>{item.emission ? calculateDaysUntilExpiry(item.emission.expiryDate) : "N/A"}</td>
                            <td>
                              {item.emission ? (
                                <span
                                  className={`badge ${
                                    calculateDaysUntilExpiry(item.emission.expiryDate) <= 0
                                      ? "bg-danger"
                                      : calculateDaysUntilExpiry(item.emission.expiryDate) <= 7
                                      ? "bg-warning"
                                      : "bg-success"
                                  }`}
                                >
                                  {calculateDaysUntilExpiry(item.emission.expiryDate) <= 0
                                    ? "Expired"
                                    : calculateDaysUntilExpiry(item.emission.expiryDate) <= 7
                                    ? "Expiring Soon"
                                    : "Active"}
                                </span>
                              ) : "N/A"}
                            </td>
                            <td>{item.revenue ? calculateDaysUntilExpiry(item.revenue.licenceEndDate) : "N/A"}</td>
                            <td>
                              {item.revenue ? (
                                <span
                                  className={`badge ${
                                    calculateDaysUntilExpiry(item.revenue.licenceEndDate) <= 0
                                      ? "bg-danger"
                                      : calculateDaysUntilExpiry(item.revenue.licenceEndDate) <= 7
                                      ? "bg-warning"
                                      : "bg-success"
                                  }`}
                                >
                                  {calculateDaysUntilExpiry(item.revenue.licenceEndDate) <= 0
                                    ? "Expired"
                                    : calculateDaysUntilExpiry(item.revenue.licenceEndDate) <= 7
                                    ? "Expiring Soon"
                                    : "Active"}
                                </span>
                              ) : "N/A"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {(emissionError || insuranceError || revenueLicenseError) && (
                    <button
                      className="btn btn-sm btn-primary ms-2"
                      onClick={() => {
                        fetchEmissionData();
                        fetchInsuranceData();
                        fetchRevenueLicenseData();
                      }}
                      disabled={isEmissionLoading || isInsuranceLoading || isRevenueLoading}
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDashboard;